import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { Beaker, FileSpreadsheet, Smartphone, Store } from 'lucide-react';
import { brand } from '@/config/brand';
import { LastCallDB } from '@/data/db';
import { initialsOf } from '@/data/people';
import { dbNameFor, rt, setRuntime, updateRuntime, type DataMode } from '@/data/runtime';
import { SAMPLE_VERSION, seedPractice } from '@/data/seed';
import { DEFAULT_SETTINGS, getSettings } from '@/data/settings';
import { setupReds } from '@/data/setup';
import { businessDateFor } from '@/domain/businessDate';
import type { Person } from '@/domain/types';
import { syncEngineFor } from '@/sync/engine';
import { Button, Card, Notice, Spinner } from '@/ui/core';
import { Logo } from '@/ui/Logo';
import { Field, TextInput } from '@/ui/form';
import { SessionContext, type SessionValue } from './providers';
import { PinPad } from './WhoDialog';
import { SIGN_OUT_IDLE_MS, stillSignedIn } from './signIn';

const MODE_KEY = 'lastcall.mode';
const USER_KEY = (mode: DataMode) => `lastcall.user.${mode}`;
/** When someone last chose their name on this device (real counts only: see signIn.ts). */
const SIGNED_IN_KEY = 'lastcall.signedIn.reds';
/** A manager with a PIN is switched back to staff after this long without a tap. */
const PIN_IDLE_MS = 15 * 60_000;

function readMode(): DataMode | null {
  try {
    const v = localStorage.getItem(MODE_KEY);
    return v === 'reds' || v === 'practice' ? v : null;
  } catch {
    return null;
  }
}

function store(key: string, value: string | null) {
  try {
    if (value == null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    /* only a convenience */
  }
}

function readStored(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

type Phase = { kind: 'choose' } | { kind: 'loading'; message: string } | { kind: 'setup' } | { kind: 'who'; people: Person[] } | { kind: 'error'; title: string; detail: string } | { kind: 'ready'; userId: string };

/** The person to start as: the last one used, unless they have a PIN (then a staff member). */
export function startingPerson(people: Person[], storedId: string | null): Person | undefined {
  const active = people.filter((p) => p.active);
  const stored = active.find((p) => p.id === storedId);
  if (stored && !stored.pinHash) return stored;
  return active.find((p) => p.role === 'staff') ?? active.find((p) => !p.pinHash) ?? stored ?? active[0];
}

export function Boot({ children }: { children: ReactNode }) {
  const [mode, setMode] = useState<DataMode | null>(readMode);
  const [phase, setPhase] = useState<Phase>(() => (readMode() ? { kind: 'loading', message: 'Opening' } : { kind: 'choose' }));

  const switchMode = useCallback((m: DataMode | null) => {
    store(MODE_KEY, m);
    window.location.assign('/');
  }, []);

  const finish = useCallback(async (db: LastCallDB, m: DataMode) => {
    const people = await db.people.toArray();
    const stored = readStored(USER_KEY(m));
    const pick = startingPerson(people, stored);
    if (!pick) {
      setPhase({ kind: 'setup' });
      return;
    }
    // A new day (after 4 a.m.): everyone signs in for themselves, so counts show who did them.
    if (m === 'reds' && !stillSignedIn(readStored(SIGNED_IN_KEY), new Date(), (await getSettings()).timezone)) {
      setPhase({ kind: 'who', people: people.filter((p) => p.active) });
      return;
    }
    // Never start as someone with a PIN unless they were the last person on this device
    // (e.g. a phone that just joined): ask who it is, with the PIN.
    if (pick.pinHash && pick.id !== stored) {
      setPhase({ kind: 'who', people: people.filter((p) => p.active) });
      return;
    }
    updateRuntime({ actor: { userId: pick.id, role: pick.role, name: pick.name } });
    setPhase({ kind: 'ready', userId: pick.id });
  }, []);

  useEffect(() => {
    if (!mode) return;
    let cancelled = false;
    (async () => {
      try {
        const db = new LastCallDB(dbNameFor(mode));
        await db.open();
        setRuntime({ db, mode, actor: { userId: 'system', role: 'owner', name: 'Setup' } });
        try {
          // Ask the browser not to clear this storage when space runs low.
          await navigator.storage?.persist?.();
        } catch {
          /* best effort */
        }
        if (mode === 'practice') {
          const version = (await db.meta.get('sampleVersion'))?.value;
          if ((await db.items.count()) > 0 && version !== SAMPLE_VERSION) {
            setPhase({ kind: 'loading', message: 'Updating the practice bar' });
            await Promise.all(db.tables.map((t) => t.clear()));
          }
          if ((await db.items.count()) === 0) {
            setPhase({ kind: 'loading', message: 'Making up nine weeks of practice numbers' });
            await seedPractice(db, businessDateFor(new Date(), DEFAULT_SETTINGS.timezone, DEFAULT_SETTINGS.cutoff));
          }
        } else if ((await db.people.count()) === 0) {
          if (!cancelled) setPhase({ kind: 'setup' });
          return;
        }
        if (!cancelled) await finish(db, mode);
        // Sharing between devices (if this device shares): runs in the background, never blocks.
        if (!cancelled && mode === 'reds') void syncEngineFor(db).start();
      } catch (err) {
        if (!cancelled)
          setPhase({
            kind: 'error',
            title: 'This tablet’s storage couldn’t be opened',
            detail: `${(err as Error)?.message ?? String(err)}. Private browsing, or blocked site data, can cause this. Nothing has been saved.`,
          });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [mode, finish]);

  if (phase.kind === 'choose')
    return (
      <Welcome
        onChoose={(m) => {
          store(MODE_KEY, m);
          setMode(m);
          setPhase({ kind: 'loading', message: 'Opening' });
        }}
      />
    );
  if (phase.kind === 'loading')
    return (
      <FullScreen>
        <Spinner label={phase.message} />
      </FullScreen>
    );
  if (phase.kind === 'error')
    return (
      <FullScreen>
        <Card className="max-w-lg">
          <Notice tone="danger" title={phase.title}>
            {phase.detail}
          </Notice>
          <div className="mt-4 flex flex-wrap gap-2">
            <Button onClick={() => window.location.reload()}>Try again</Button>
            <Button onClick={() => switchMode(null)}>Back to the start</Button>
          </div>
        </Card>
      </FullScreen>
    );
  if (phase.kind === 'who')
    return (
      <FullScreen>
        <Card className="w-full max-w-lg">
          <Logo className="mx-auto h-16" />
          <h1 className="mt-3 text-2xl font-bold">Who is using this device?</h1>
          {mode === 'reds' && <p className="mt-1 text-base text-muted">Tap your own name. Every day after 4 a.m. the app asks again, so the counts show who did them.</p>}
          <PickPerson
            people={phase.people}
            onPicked={(p) => {
              store(USER_KEY(mode!), p.id);
              if (mode === 'reds') store(SIGNED_IN_KEY, new Date().toISOString());
              updateRuntime({ actor: { userId: p.id, role: p.role, name: p.name } });
              setPhase({ kind: 'ready', userId: p.id });
            }}
          />
        </Card>
      </FullScreen>
    );
  if (phase.kind === 'setup')
    return (
      <RedsSetup
        onBack={() => switchMode(null)}
        onDone={async (owner) => {
          if (owner) {
            store(USER_KEY('reds'), owner.id);
            store(SIGNED_IN_KEY, new Date().toISOString());
          }
          await finish(rt().db, 'reds');
          if (!owner) void syncEngineFor(rt().db).start();
        }}
      />
    );
  return (
    <SessionShell mode={mode!} initialUserId={phase.userId} switchMode={switchMode}>
      {children}
    </SessionShell>
  );
}

function SessionShell({ mode, initialUserId, switchMode, children }: { mode: DataMode; initialUserId: string; switchMode: (m: DataMode | null) => void; children: ReactNode }) {
  const [userId, setUserIdState] = useState(initialUserId);
  const people = useLiveQuery(() => rt().db.people.toArray(), [], undefined);
  const settings = useLiveQuery(() => getSettings(), [], DEFAULT_SETTINGS);
  const person = people?.find((u) => u.id === userId);
  // Switched by the app (PIN timeout, removed person): not someone signing in.
  const pickUserId = useCallback(
    (id: string) => {
      store(USER_KEY(mode), id);
      setUserIdState(id);
    },
    [mode],
  );
  // Someone chose their own name.
  const setUserId = useCallback(
    (id: string) => {
      if (mode === 'reds') store(SIGNED_IN_KEY, new Date().toISOString());
      pickUserId(id);
    },
    [mode, pickUserId],
  );
  useEffect(() => {
    if (person) updateRuntime({ actor: { userId: person.id, role: person.role, name: person.name } });
  }, [person]);

  // A manager with a PIN goes back to a staff member after a while without a tap.
  const lastTap = useRef(Date.now());
  useEffect(() => {
    const onTap = () => (lastTap.current = Date.now());
    window.addEventListener('pointerdown', onTap, { passive: true });
    window.addEventListener('keydown', onTap);
    const t = window.setInterval(() => {
      if (!person?.pinHash || Date.now() - lastTap.current < PIN_IDLE_MS || !people) return;
      const next = startingPerson(people, null);
      if (next && next.id !== person.id) {
        pickUserId(next.id);
        if (window.location.pathname.startsWith('/admin')) window.location.assign('/');
      }
    }, 30_000);
    return () => {
      window.removeEventListener('pointerdown', onTap);
      window.removeEventListener('keydown', onTap);
      window.clearInterval(t);
    };
  }, [person, people, pickUserId]);

  // After 4 a.m. whoever was signed in is signed out, once the device has been left alone
  // (never in the middle of typing a count). Opening the app again asks who's using it.
  useEffect(() => {
    if (mode !== 'reds') return;
    const check = () => {
      if (document.visibilityState === 'hidden' || Date.now() - lastTap.current < SIGN_OUT_IDLE_MS) return;
      if (!stillSignedIn(readStored(SIGNED_IN_KEY), new Date(), settings.timezone)) window.location.assign('/');
    };
    const t = window.setInterval(check, 30_000);
    document.addEventListener('visibilitychange', check);
    return () => {
      window.clearInterval(t);
      document.removeEventListener('visibilitychange', check);
    };
  }, [mode, settings.timezone]);

  const value = useMemo<SessionValue | null>(() => {
    if (!person || !people) return null;
    return { mode, actor: { userId: person.id, role: person.role, name: person.name }, people, settings, switchUser: setUserId, switchMode };
  }, [mode, person, people, settings, setUserId, switchMode]);
  if (people && !person) {
    // The remembered person was removed: start again with someone who's still here.
    const next = startingPerson(people, null);
    if (next) {
      queueMicrotask(() => pickUserId(next.id));
      return (
        <FullScreen>
          <Spinner label="Loading" />
        </FullScreen>
      );
    }
  }
  if (!value)
    return (
      <FullScreen>
        <Spinner label="Loading" />
      </FullScreen>
    );
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

function PickPerson({ people, onPicked }: { people: Person[]; onPicked: (p: Person) => void }) {
  const [asking, setAsking] = useState<Person | null>(null);
  if (asking) return <div className="mt-4"><p className="mb-3 text-center text-lg font-semibold">PIN for {asking.name}</p><PinPad person={asking} onOk={() => onPicked(asking)} onCancel={() => setAsking(null)} /></div>;
  return (
    <ul className="mt-4 grid gap-2">
      {people.map((p) => (
        <li key={p.id}>
          <Button size="xl" block onClick={() => (p.pinHash ? setAsking(p) : onPicked(p))}>
            {p.name}
          </Button>
        </li>
      ))}
    </ul>
  );
}

function FullScreen({ children }: { children: ReactNode }) {
  return <div className="flex min-h-dvh items-center justify-center p-4">{children}</div>;
}

function Welcome({ onChoose }: { onChoose: (m: DataMode) => void }) {
  return (
    <div className="mx-auto flex min-h-dvh max-w-4xl flex-col justify-center gap-6 p-4 md:gap-8 md:p-8">
      <div className="flex flex-col items-center gap-3 text-center">
        <Logo className="h-32 md:h-44" />
        <h1 className="text-2xl font-bold md:text-3xl">The nightly count</h1>
        <p className="text-muted md:text-lg">
          {brand.name} · {brand.tagline}
        </p>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <ModeCard icon={<Store className="size-8" aria-hidden />} title={`${brand.business} — real counts`} onClick={() => onChoose('reds')} cta="Start" primary>
          The real nightly counts for the bar. Saved on this tablet, and each week saves as your Excel count sheet.
        </ModeCard>
        <ModeCard icon={<Beaker className="size-8" aria-hidden />} title="Practice" onClick={() => onChoose('practice')} cta="Practise">
          Red’s drink lists with made-up numbers, to learn the app or show it to someone. Kept apart from the real counts.
        </ModeCard>
      </div>
      <p className="text-center text-sm text-muted">You can switch later in the manager area (Settings).</p>
    </div>
  );
}

function ModeCard({ icon, title, children, onClick, cta, primary }: { icon: ReactNode; title: string; children: ReactNode; onClick: () => void; cta: string; primary?: boolean }) {
  return (
    <Card className="flex flex-col gap-3">
      <div className="text-accent">{icon}</div>
      <h2 className="text-xl font-semibold">{title}</h2>
      <p className="flex-1 text-base text-muted">{children}</p>
      <Button variant={primary ? 'primary' : 'secondary'} size="lg" onClick={onClick} block>
        {cta}
      </Button>
    </Card>
  );
}

function RedsSetup({ onDone, onBack }: { onDone: (owner: Person | null) => void; onBack: () => void }) {
  const [name, setName] = useState('');
  const [initials, setInitials] = useState('');
  const [pin, setPin] = useState('');
  const [start, setStart] = useState<'lists' | 'excel' | 'join'>('lists');
  const restore = start === 'excel';
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  return (
    <FullScreen>
      <Card className="w-full max-w-lg">
        <Logo className="mx-auto h-20" />
        <h1 className="mt-3 text-2xl font-bold">Set up this tablet</h1>
        <p className="mt-1 text-base text-muted">The first device: you’ll be the owner. A second tablet or phone: choose “Join”.</p>
        <form
          className="mt-4 flex flex-col gap-4"
          onSubmit={async (e) => {
            e.preventDefault();
            setBusy(true);
            setError(null);
            try {
              if (start === 'join') {
                const engine = syncEngineFor(rt().db);
                await engine.join(code);
                await engine.syncNow();
                const status = engine.getStatus();
                if (status.state === 'error' || status.state === 'offline') throw new Error(status.message ?? 'Couldn’t get the records from the other device.');
                if ((await rt().db.people.count()) === 0) throw new Error('Joined, but nothing has been shared from the other device yet. Check sharing is on there, then try again.');
                onDone(null);
                return;
              }
              const owner = await setupReds({ name, initials, pin: pin || null, withLists: !restore });
              onDone(owner);
            } catch (err) {
              setError((err as Error).message);
              setBusy(false);
            }
          }}
        >
          <fieldset className="flex flex-col gap-2">
            <legend className="mb-1 text-sm font-semibold">How do you want to start?</legend>
            <label className="flex min-h-12 cursor-pointer items-start gap-3 rounded-xl border-2 border-border p-3 has-[:checked]:border-accent">
              <input type="radio" name="start" aria-label="Start with Red’s lists" checked={start === 'lists'} onChange={() => setStart('lists')} className="mt-1 size-5" />
              <span>
                <strong className="flex items-center gap-2">Start with Red’s lists</strong>
                <span className="block text-sm text-muted">The beers and liquors from the count sheets, in the same order. Change them any time.</span>
              </span>
            </label>
            <label className="flex min-h-12 cursor-pointer items-start gap-3 rounded-xl border-2 border-border p-3 has-[:checked]:border-accent">
              <input type="radio" name="start" aria-label="Join a tablet or phone that’s already set up" checked={start === 'join'} onChange={() => setStart('join')} className="mt-1 size-5" />
              <span>
                <strong className="flex items-center gap-2"><Smartphone className="size-5" aria-hidden /> Join a tablet or phone that’s already set up</strong>
                <span className="block text-sm text-muted">Type the join code shown on the other device (Manager area → Excel files & settings → Sharing). Everything comes over.</span>
              </span>
            </label>
            <label className="flex min-h-12 cursor-pointer items-start gap-3 rounded-xl border-2 border-border p-3 has-[:checked]:border-accent">
              <input type="radio" name="start" aria-label="Bring everything back from an Excel file" checked={start === 'excel'} onChange={() => setStart('excel')} className="mt-1 size-5" />
              <span>
                <strong className="flex items-center gap-2"><FileSpreadsheet className="size-5" aria-hidden /> Bring everything back from an Excel file</strong>
                <span className="block text-sm text-muted">For a new or reset tablet with no other device to join: open the latest week’s file saved from this app.</span>
              </span>
            </label>
          </fieldset>
          {start === 'join' ? (
            <Field label="Join code from the other device" htmlFor="join-code" hint="8 letters and numbers, like K7QF-3MZD (it works for 15 minutes). No device left? Type the owner’s recovery code instead.">
              <TextInput id="join-code" value={code} onChange={(e) => setCode(e.target.value.toUpperCase().slice(0, 60))} autoComplete="off" autoCapitalize="characters" className="text-center text-2xl tracking-widest" />
            </Field>
          ) : (
            <>
              <Field label="Your name" htmlFor="owner-name" required>
                <TextInput id="owner-name" value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" />
              </Field>
              <Field label="Your initials" htmlFor="owner-initials" hint="They go in the “Count Initial” row of the sheet.">
                <TextInput id="owner-initials" value={initials} placeholder={initialsOf(name) || 'e.g. KP'} onChange={(e) => setInitials(e.target.value.toUpperCase())} maxLength={4} className="w-32" />
              </Field>
              <Field label="A 4-digit PIN (recommended)" htmlFor="owner-pin" hint="Keeps staff out of the manager area. Leave empty for none — you can set it later.">
                <TextInput id="owner-pin" value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))} inputMode="numeric" autoComplete="off" className="w-32 text-center text-xl tracking-[0.5em]" />
              </Field>
            </>
          )}
          {error && <Notice tone="danger">{error}</Notice>}
          <div className="flex gap-2">
            <Button onClick={onBack} size="lg">
              Back
            </Button>
            <Button type="submit" variant="primary" size="lg" busy={busy} className="flex-1" disabled={start === 'join' ? ![8, 40].includes(code.replace(/[^A-Z0-9]/g, '').length) && code.replace(/[^A-Z0-9]/g, '').length < 30 : !name.trim() || (pin !== '' && pin.length !== 4)}>
              {start === 'join' ? 'Join' : 'Set up'}
            </Button>
          </div>
        </form>
      </Card>
    </FullScreen>
  );
}
